import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { HardDrive, Info } from 'lucide-react'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { FeatheredScrollArea } from '@/components/ui/feathered-scroll-area'
import type { ConversionOption } from '../api'

/**
 * Choose what a recovery's disk images are converted to on egress.
 *
 * Compute is vaulted in whatever format the source platform produced, and conversion happens on
 * EGRESS rather than ingress — converting everything to everything at backup time would multiply
 * vault storage for images most recoveries never ask for. So the question has to be asked here,
 * at request time.
 *
 * WHAT THIS PROMISES IS A CONVERTED IMAGE, NOT A BOOTING VM. A converted disk still carries the
 * original platform's storage drivers, so a Windows image moved between hypervisors can convert
 * perfectly and still stop at a boot error. The copy underneath says so rather than letting the
 * customer read "converted to VMware" as "will start on VMware".
 *
 * The target list is per object and comes from the server, which reads it from ConversionMatrix —
 * the same table the egress Lambdas and VSCopyFile read. Nothing is offered here that cannot
 * actually be produced.
 *
 * SOURCE FORMAT IS A DECLARATION OR A GUESS, and the difference is shown. When the agent wrote
 * `format` into metadata.json we know the format, subformat and all. When it did not, we inferred
 * it from the saved file names — which cannot see a VMDK's subformat, so we take the safe side
 * and offer less. Marking the inferred ones tells the customer why a machine they expected to see
 * has fewer options than its neighbour.
 */

export type ConversionMode = 'none' | 'all' | 'per-object'

interface ConversionFormatPickerProps {
    options: ConversionOption[]
    isLoading?: boolean
    mode: ConversionMode
    onModeChange: (mode: ConversionMode) => void
    /** Targets applied to every convertible object when mode is 'all'. */
    allTargets: string[]
    onAllTargetsChange: (targets: string[]) => void
    /** ObjectID -> targets, used when mode is 'per-object'. */
    perObject: Record<string, string[]>
    onPerObjectChange: (next: Record<string, string[]>) => void
}

/** Human label for a target format code. Codes are the wire contract; these are only for reading. */
export function useFormatLabel() {
    const { t } = useTranslation()
    return (code: string) => {
        switch (code) {
            case 'vmdk-sparse': return t('objects.formatVmdkSparse', 'VMware (VMDK)')
            case 'vmdk-flat': return t('objects.formatVmdkFlat', 'VMware (VMDK, flat)')
            case 'vmdk-streamoptimized': return t('objects.formatVmdkStream', 'VMware (VMDK, stream-optimised)')
            case 'vhdx': return t('objects.formatVhdx', 'Hyper-V (VHDX)')
            case 'vhd-fixed': return t('objects.formatVhdFixed', 'Azure / Hyper-V (VHD, fixed)')
            case 'vhd-dynamic': return t('objects.formatVhdDynamic', 'Hyper-V (VHD, dynamic)')
            case 'ova': return t('objects.formatOva', 'OVA appliance')
            case 'raw': return t('objects.formatRaw', 'Raw (as vaulted)')
            case 'unknown': return t('objects.formatUnknown', 'Not identified')
            case 'vmdk-unspecified': return t('objects.formatVmdkUnspecified', 'VMware (VMDK)')
            case 'vhd-unspecified': return t('objects.formatVhdUnspecified', 'VHD')
            default: return code
        }
    }
}

export function ConversionFormatPicker({
    options,
    isLoading,
    mode,
    onModeChange,
    allTargets,
    onAllTargetsChange,
    perObject,
    onPerObjectChange,
}: ConversionFormatPickerProps) {
    const { t } = useTranslation()
    const label = useFormatLabel()

    const convertible = useMemo(() => options.filter((o) => o.canConvert), [options])
    const noDiskImage = useMemo(() => options.filter((o) => !o.hasDiskImage), [options])
    const unreadable = useMemo(
        () => options.filter((o) => o.hasDiskImage && !o.canConvert),
        [options],
    )
    const incompleteEvidence = useMemo(
        () => options.filter((o) => o.fileListTruncated && !o.canConvert),
        [options],
    )

    // Targets offered for "same for all" are the INTERSECTION, not the union. A union would let
    // the customer tick a target that only some objects can produce, and the rest would silently
    // convert to nothing — the failure this whole feature exists to avoid. If the intersection is
    // empty the picker says so and per-object is the only honest way through.
    const sharedTargets = useMemo(() => {
        if (convertible.length === 0) return []
        return convertible
            .map((o) => o.allowedTargets)
            .reduce((acc, cur) => acc.filter((code) => cur.includes(code)))
    }, [convertible])

    if (isLoading) {
        return (
            <div className="space-y-2">
                <Label>{t('objects.conversionTitle', 'Convert disk images on egress')}</Label>
                <p className="text-xs text-muted-foreground">{t('common.loading', 'Loading…')}</p>
            </div>
        )
    }

    // Nothing in this request carries a disk image we can read, so there is no question to ask.
    // Saying that outright beats an empty control the customer has to interpret.
    if (convertible.length === 0) {
        return (
            <div className="space-y-2">
                <Label>{t('objects.conversionTitle', 'Convert disk images on egress')}</Label>
                <p className="text-xs text-muted-foreground">
                    {t(
                        'objects.conversionNothingToConvert',
                        'Nothing in this recovery has a disk image that can be converted. Everything is recovered in the format it was vaulted in.',
                    )}
                </p>
                {incompleteEvidence.length > 0 && (
                    <p className="text-xs text-amber-600 dark:text-amber-500">
                        {t('objects.conversionIncompleteEvidence', {
                            count: incompleteEvidence.length,
                            defaultValue:
                                'Saved file list incomplete for {{count}} object(s), so their disk formats could not be identified.',
                        })}
                    </p>
                )}
            </div>
        )
    }

    const toggle = (list: string[], code: string) =>
        list.includes(code) ? list.filter((c) => c !== code) : [...list, code]

    return (
        <div className="space-y-3">
            <div className="flex items-baseline justify-between gap-3">
                <Label>{t('objects.conversionTitle', 'Convert disk images on egress')}</Label>
                <span className="text-xs text-muted-foreground">
                    {t('objects.conversionConvertibleCount', {
                        count: convertible.length,
                        total: options.length,
                        defaultValue: '{{count}} of {{total}} can be converted',
                    })}
                </span>
            </div>

            <div className="flex flex-wrap gap-2">
                <Button
                    type="button"
                    size="sm"
                    variant={mode === 'none' ? 'default' : 'outline'}
                    onClick={() => onModeChange('none')}
                >
                    {t('objects.conversionNone', 'No conversion')}
                </Button>
                <Button
                    type="button"
                    size="sm"
                    variant={mode === 'all' ? 'default' : 'outline'}
                    onClick={() => onModeChange('all')}
                    disabled={sharedTargets.length === 0}
                >
                    {t('objects.conversionAll', 'Same for all')}
                </Button>
                <Button
                    type="button"
                    size="sm"
                    variant={mode === 'per-object' ? 'default' : 'outline'}
                    onClick={() => onModeChange('per-object')}
                >
                    {t('objects.conversionPerObject', 'Choose per object')}
                </Button>
            </div>

            {mode === 'none' && (
                <p className="text-xs text-muted-foreground">
                    {t(
                        'objects.conversionNoneHelp',
                        'Everything is recovered in the format it was vaulted in. Our restore agent can use that format directly.',
                    )}
                </p>
            )}

            {mode === 'all' && (
                <div className="space-y-2 rounded-md border p-3">
                    {sharedTargets.length === 0 ? (
                        <p className="text-xs text-amber-600 dark:text-amber-500">
                            {t(
                                'objects.conversionNoSharedTarget',
                                'These objects are vaulted in different formats with no target in common. Choose per object instead.',
                            )}
                        </p>
                    ) : (
                        <>
                            <div className="flex flex-wrap gap-x-6 gap-y-2">
                                {sharedTargets.map((code) => (
                                    <label key={code} className="flex items-center gap-2 text-sm">
                                        <Checkbox
                                            checked={allTargets.includes(code)}
                                            onCheckedChange={() => onAllTargetsChange(toggle(allTargets, code))}
                                        />
                                        {label(code)}
                                    </label>
                                ))}
                            </div>
                            <p className="text-xs text-muted-foreground">
                                {t('objects.conversionAllHelp', {
                                    count: convertible.length,
                                    defaultValue: 'Applied to all {{count}} objects that can be converted.',
                                })}
                            </p>
                        </>
                    )}
                </div>
            )}

            {mode === 'per-object' && (
                <FeatheredScrollArea className="rounded-md border" viewportClassName="max-h-64">
                    <div className="divide-y">
                        {convertible.map((o) => (
                            <div key={o.objectId} className="flex flex-wrap items-center gap-x-4 gap-y-2 p-3">
                                <div className="min-w-[12rem] flex-1">
                                    <div className="flex items-center gap-2">
                                        <HardDrive className="h-3.5 w-3.5 text-muted-foreground" />
                                        <span className="text-sm font-medium">{o.objectId}</span>
                                    </div>
                                    <span className="text-xs text-muted-foreground">
                                        {t('objects.conversionVaultedAs', 'Vaulted as')} {label(o.sourceFormat)}
                                        {o.formatSource === 'filelist' && (
                                            <>
                                                {' '}
                                                <span
                                                    className="text-amber-600 dark:text-amber-500"
                                                    title={t(
                                                        'objects.conversionInferredHelp',
                                                        'Worked out from the saved file names because the backup did not record its format. File names cannot show a VMDK’s exact variant, so fewer targets are offered.',
                                                    )}
                                                >
                                                    ({t('objects.conversionInferred', 'inferred')})
                                                </span>
                                            </>
                                        )}
                                        {o.classification ? ` · ${o.classification}` : ''}
                                    </span>
                                </div>
                                <div className="flex flex-wrap gap-x-4 gap-y-1">
                                    {o.allowedTargets.map((code) => (
                                        <label key={code} className="flex items-center gap-2 text-sm">
                                            <Checkbox
                                                checked={(perObject[o.objectId] || []).includes(code)}
                                                onCheckedChange={() =>
                                                    onPerObjectChange({
                                                        ...perObject,
                                                        [o.objectId]: toggle(perObject[o.objectId] || [], code),
                                                    })
                                                }
                                            />
                                            {label(code)}
                                        </label>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </FeatheredScrollArea>
            )}

            {mode !== 'none' && (
                <p className="flex items-start gap-2 text-xs text-muted-foreground">
                    <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                    {t(
                        'objects.conversionBootWarning',
                        'Conversion produces a disk image in the chosen format. It does not install drivers for the new platform, so a converted image may still need its boot configuration adjusting before it starts.',
                    )}
                </p>
            )}

            {(noDiskImage.length > 0 || unreadable.length > 0) && (
                <div className="flex flex-wrap gap-2">
                    {noDiskImage.length > 0 && (
                        <Badge variant="outline" className="font-normal">
                            {t('objects.conversionNoDiskImage', {
                                count: noDiskImage.length,
                                defaultValue: '{{count}} with no disk image',
                            })}
                        </Badge>
                    )}
                    {unreadable.length > 0 && (
                        <Badge variant="outline" className="font-normal">
                            {t('objects.conversionUnreadable', {
                                count: unreadable.length,
                                defaultValue: '{{count}} in a format we cannot convert',
                            })}
                        </Badge>
                    )}
                </div>
            )}
        </div>
    )
}

/**
 * Turn the picker's state into the wire payload: ObjectID -> comma-free array of target codes.
 * Objects absent from the result recover in raw, which is the default and needs no entry.
 */
export function buildConvertFormats(
    mode: ConversionMode,
    options: ConversionOption[],
    allTargets: string[],
    perObject: Record<string, string[]>,
): Record<string, string[]> | undefined {
    if (mode === 'none') return undefined
    const convertible = options.filter((o) => o.canConvert)

    if (mode === 'all') {
        if (allTargets.length === 0) return undefined
        const out: Record<string, string[]> = {}
        for (const o of convertible) {
            // Intersected again here, not trusted from the control: the shared list is computed
            // from the same data, but this is the value that gets stored.
            const targets = allTargets.filter((code) => o.allowedTargets.includes(code))
            if (targets.length > 0) out[o.objectId] = targets
        }
        return Object.keys(out).length > 0 ? out : undefined
    }

    const out: Record<string, string[]> = {}
    for (const o of convertible) {
        const targets = (perObject[o.objectId] || []).filter((code) => o.allowedTargets.includes(code))
        if (targets.length > 0) out[o.objectId] = targets
    }
    return Object.keys(out).length > 0 ? out : undefined
}
